import { where, Timestamp, orderBy, doc, setDoc, writeBatch, arrayUnion, arrayRemove, deleteField } from 'firebase/firestore';
import { FirestoreService, type AuditContext } from '@/lib/firestore';
import { COLLECTION_NAMES } from '@/lib/collections';
import { db } from '@/lib/firebase';
import { activityEventService } from '@/shared/services/activityEventService';
import { Logger } from '@/lib/logger';
import { workflowSpotService } from './workflowSpotService';
import { hiringSpotService } from '@/shared/services/hiringSpotService';
import type {
  ProgramData,
  TeamData,
  WorkflowData,
  CreateProgramData,
  CreateTeamData,
  CreateWorkflowData,
  UpdateProgramData,
  UpdateTeamData,
  UpdateWorkflowData,
  ProgramInfo,
  TeamInfo,
  WorkflowInfo
} from '../types/threeCollectionWorkflow';
import {
  CreateProgramDataSchema,
  CreateTeamDataSchema,
  CreateWorkflowDataSchema
} from '../types/threeCollectionWorkflow';

/**
 * ThreeCollectionWorkflowService - Service for managing the new three-collection workflow structure
 * 
 * Collections:
 * 1. workflow_team_programs - Top level programs (Document ID = User ID)
 * 2. workflow_team_teams - Teams within programs (Document ID = User ID) 
 * 3. workflow_team_workflows - Individual workflows (Document ID = Generated UID)
 * 
 * Features:
 * - CRUD operations for all three collections
 * - Cross-collection relationship management
 * - Data aggregation for UI components
 * - Real-time subscriptions
 * - Comprehensive validation and error handling
 */
const logger = Logger.createScoped('threeCollectionWorkflowService');

class ThreeCollectionWorkflowService {
  private programService: FirestoreService<ProgramData>;
  private teamService: FirestoreService<TeamData>;
  private workflowService: FirestoreService<WorkflowData>;

  constructor() {
    this.programService = new FirestoreService<ProgramData>(COLLECTION_NAMES.WORKFLOW_TEAM_PROGRAMS, {
      enableAudit: true,
    });
    this.teamService = new FirestoreService<TeamData>(COLLECTION_NAMES.WORKFLOW_TEAM_TEAMS, {
      enableAudit: true,
    });
    this.workflowService = new FirestoreService<WorkflowData>(COLLECTION_NAMES.WORKFLOW_TEAM_WORKFLOWS, {
      enableAudit: true,
    });
  }

  // ============================================================================
  // PROGRAM OPERATIONS
  // ============================================================================

  /**
   * Create a new program
   */
  async createProgram(
    data: CreateProgramData,
    userId: string,
    userName: string,
    auditContext: AuditContext
  ): Promise<string> {
    try {
      // Validate input data
      const validatedData = CreateProgramDataSchema.parse(data);
      
      const now = Timestamp.now();
      const documentId = validatedData.owner_id; // Use owner_id as document ID
      
      // Check if program already exists for this user
      const existingProgram = await this.programService.get(documentId);
      if (existingProgram) {
        throw new Error(`Program already exists for user ${validatedData.owner_id}`);
      }

      const program: Omit<ProgramData, 'id'> = {
        program_name: validatedData.program_name,
        owner_id: validatedData.owner_id,
        owner_name: validatedData.owner_name,
        ...(validatedData.description !== undefined && { description: validatedData.description }),
        team_ids: [], // Start with empty team list
        created_by: userId,
        created_by_name: userName,
        created_at: now,
        updated_at: now,
        last_modified_by: userId,
        last_modified_by_name: userName,
        version: 1,
        ...(validatedData.metadata !== undefined && { metadata: validatedData.metadata }),
      };

      await this.createProgramWithId(documentId, program, auditContext);
      return documentId;
    } catch (error) {
      console.error('Error creating program:', error);
      throw error;
    }
  }

  /**
   * Create program with specific ID
   */
  private async createProgramWithId(
    id: string,
    data: Omit<ProgramData, 'id'>,
    _auditContext: AuditContext
  ): Promise<void> {
    try {
      const now = Timestamp.now();
      const docData = {
        ...data,
        id,
        createdAt: now,
        updatedAt: now,
      };

      const docRef = doc(db, COLLECTION_NAMES.WORKFLOW_TEAM_PROGRAMS, id);
      await setDoc(docRef, docData);
    } catch (error) {
      console.error('Error creating program with ID:', error);
      throw error;
    }
  }

  /**
   * Get program by ID
   */
  async getProgram(programId: string): Promise<ProgramData | null> {
    try {
      return await this.programService.get(programId);
    } catch (error) {
      console.error('Error getting program:', error);
      throw error;
    }
  }

  /**
   * Update program
   */
  async updateProgram(
    programId: string,
    data: UpdateProgramData,
    userId: string,
    userName: string,
    auditContext: AuditContext
  ): Promise<void> {
    try {
      const now = Timestamp.now();
      
      const updatePayload: Partial<ProgramData> = {
        updated_at: now,
        last_modified_by: userId,
        last_modified_by_name: userName,
      };

      if (data.program_name) updatePayload.program_name = data.program_name;
      if (data.description !== undefined) updatePayload.description = data.description;
      if (data.metadata) updatePayload.metadata = data.metadata;

      // Handle version increment
      const currentDoc = await this.programService.get(programId);
      if (currentDoc) {
        updatePayload.version = currentDoc.version + 1;
      } else {
        throw new Error(`Program not found: ${programId}`);
      }

      await this.programService.update(programId, updatePayload, auditContext);
    } catch (error) {
      console.error('Error updating program:', error);
      throw error;
    }
  }

  /**
   * Delete program (and all associated teams and workflows)
   */
  async deleteProgram(
    programId: string,
    _auditContext: AuditContext
  ): Promise<void> {
    try {
      // Get program to verify it exists
      const program = await this.programService.get(programId);
      if (!program) {
        throw new Error(`Program not found: ${programId}`);
      }

      // Use batch to delete program, teams, and workflows atomically
      const batch = writeBatch(db);

      // Delete all workflows, workflow spots, hiring spots, and teams in this program
      const teams = await this.getTeamsByProgram(programId);
      for (const team of teams) {
        const [workflows, teamWorkflowSpots, teamHiringSpots] = await Promise.all([
          this.getWorkflowsByTeam(team.id),
          workflowSpotService.getWorkflowSpotsByTeam(team.id),
          hiringSpotService.getHiringSpotsByTeam(team.id),
        ]);

        for (const workflow of workflows) {
          const workflowRef = doc(db, COLLECTION_NAMES.WORKFLOW_TEAM_WORKFLOWS, workflow.id);
          batch.delete(workflowRef);
        }

        for (const spot of teamWorkflowSpots) {
          const spotRef = doc(db, COLLECTION_NAMES.WORKFLOW_SPOTS, spot.id);
          batch.delete(spotRef);
        }

        for (const spot of teamHiringSpots) {
          const spotRef = doc(db, COLLECTION_NAMES.HIRING_SPOTS, spot.id);
          batch.delete(spotRef);
        }

        // Delete team
        const teamRef = doc(db, COLLECTION_NAMES.WORKFLOW_TEAM_TEAMS, team.id);
        batch.delete(teamRef);
      }

      // Delete program
      const programRef = doc(db, COLLECTION_NAMES.WORKFLOW_TEAM_PROGRAMS, programId);
      batch.delete(programRef);

      await batch.commit();
    } catch (error) {
      console.error('Error deleting program:', error);
      throw error;
    }
  }

  /**
   * Get all programs
   */
  async getAllPrograms(): Promise<ProgramData[]> {
    try {
      const constraints = [orderBy('updated_at', 'desc')];
      return await this.programService.list(constraints);
    } catch (error) {
      console.error('Error getting all programs:', error);
      throw error;
    }
  }

  // ============================================================================
  // TEAM OPERATIONS
  // ============================================================================

  /**
   * Create a new team
   */
  async createTeam(
    data: CreateTeamData,
    userId: string,
    userName: string,
    _auditContext: AuditContext
  ): Promise<string> {
    try {
      // Validate input data
      const validatedData = CreateTeamDataSchema.parse(data);
      
      const now = Timestamp.now();
      const documentId = validatedData.owner_id; // Use owner_id as document ID
      
      // Check if team already exists for this user
      const existingTeam = await this.teamService.get(documentId);
      if (existingTeam) {
        const errorMsg = `Failed to create team. This team already exists under ${existingTeam.program_name}`;
        console.error('[threeCollectionWorkflowService] Duplicate team detected:', errorMsg);
        throw new Error(errorMsg);
      }

      // Verify program exists
      const program = await this.programService.get(validatedData.program_id);
      if (!program) {
        throw new Error(`Program not found: ${validatedData.program_id}`);
      }

      const team: Omit<TeamData, 'id'> = {
        team_name: validatedData.team_name,
        owner_id: validatedData.owner_id,
        owner_name: validatedData.owner_name,
        program_id: validatedData.program_id,
        program_name: validatedData.program_name,
        workflow_ids: [], // Start with empty workflow list
        notes: {
          content: validatedData.notes?.content || '',
          last_updated: now,
          last_updated_by: userId,
          last_updated_by_name: userName,
          word_count: validatedData.notes?.content ? validatedData.notes.content.split(/\s+/).length : 0,
          character_count: validatedData.notes?.content?.length || 0,
        },
        created_by: userId,
        created_by_name: userName,
        created_at: now,
        updated_at: now,
        last_modified_by: userId,
        last_modified_by_name: userName,
        version: 1,
        ...(validatedData.metadata && { metadata: validatedData.metadata }),
      };

      // Use batch to create team and update program's team_ids
      const batch = writeBatch(db);

      // Create team
      const teamRef = doc(db, COLLECTION_NAMES.WORKFLOW_TEAM_TEAMS, documentId);
      batch.set(teamRef, { ...team, id: documentId });

      // Update program's team_ids
      const programRef = doc(db, COLLECTION_NAMES.WORKFLOW_TEAM_PROGRAMS, validatedData.program_id);
      batch.update(programRef, {
        team_ids: [...program.team_ids, documentId],
        updated_at: now,
        last_modified_by: userId,
        last_modified_by_name: userName,
        version: program.version + 1,
      });

      await batch.commit();
      
      // Emit activity event for team creation
      try {
        await activityEventService.emitCreation({
          domain: 'workflow',
          event_type: 'team.created',
          actor: {
            user_id: userId,
            user_name: userName,
          },
          subject: {
            resource_type: 'team',
            resource_id: documentId,
            resource_name: validatedData.team_name,
          },
          message: `Created team '${validatedData.team_name}'${validatedData.program_name ? ` in ${validatedData.program_name}'s program` : ''}`,
          context: {
            program_id: validatedData.program_id,
            program_name: validatedData.program_name,
            team_id: documentId,
            team_name: validatedData.team_name,
          },
          metadata: {
            affected_user_ids: [userId, validatedData.owner_id],
            affected_workflow_ids: [],
            affected_employee_ids: [],
            tags: ['workflow', 'team', 'created'],
            visibility: 'program',
          },
        });
      } catch (eventError) {
        logger.error('Failed to emit team.created event', eventError);
        // Don't throw - event emission failure shouldn't break the main operation
      }
      
      return documentId;
    } catch (error) {
      console.error('Error creating team:', error);
      // Re-throw the error with its message intact
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Failed to create team: ' + String(error));
    }
  }

  /**
   * Get team by ID
   */
  async getTeam(teamId: string): Promise<TeamData | null> {
    try {
      return await this.teamService.get(teamId);
    } catch (error) {
      console.error('Error getting team:', error);
      throw error;
    }
  }

  /**
   * Update team
   */
  async updateTeam(
    teamId: string,
    data: UpdateTeamData,
    userId: string,
    userName: string,
    auditContext: AuditContext
  ): Promise<void> {
    try {
      const now = Timestamp.now();
      
      const updatePayload: Partial<TeamData> = {
        updated_at: now,
        last_modified_by: userId,
        last_modified_by_name: userName,
      };

      if (data.team_name) updatePayload.team_name = data.team_name;
      if (data.metadata) updatePayload.metadata = data.metadata;
      if (data.slack_channel_id !== undefined) {
        (updatePayload as any).slack_channel_id = data.slack_channel_id === null
          ? deleteField()
          : data.slack_channel_id;
      }

      // Handle version increment
      const currentDoc = await this.teamService.get(teamId);
      if (currentDoc) {
        updatePayload.version = currentDoc.version + 1;
      } else {
        throw new Error(`Team not found: ${teamId}`);
      }

      // Track if notes are being updated for activity event
      let notesUpdated = false;
      let previousNotesContent = '';
      let newNotesContent = '';
      
      // Handle notes update
      if (data.notes && data.notes.content !== undefined) {
        notesUpdated = true;
        previousNotesContent = currentDoc.notes?.content || '';
        newNotesContent = data.notes.content;
        
        updatePayload.notes = {
          content: data.notes.content,
          last_updated: now,
          last_updated_by: userId,
          last_updated_by_name: userName,
          word_count: data.notes.content.split(/\s+/).length,
          character_count: data.notes.content.length,
          version_history: currentDoc.notes?.version_history || [],
        };
      }

      await this.teamService.update(teamId, updatePayload, auditContext);
      
      // Emit activity event for notes update
      if (notesUpdated) {
        try {
          await activityEventService.emitUpdate({
            domain: 'workflow',
            event_type: 'team_notes.updated',
            actor: {
              user_id: userId,
              user_name: userName,
            },
            subject: {
              resource_type: 'team_notes',
              resource_id: teamId,
              resource_name: `${currentDoc.team_name} Notes`,
            },
            message: `Updated notes for ${currentDoc.team_name}`,
            context: {
              program_id: currentDoc.program_id,
              program_name: currentDoc.program_name,
              team_id: teamId,
              team_name: currentDoc.team_name,
            },
            fields_changed: [
              {
                field_name: 'content',
                old_value: `${previousNotesContent.length} characters`,
                new_value: `${newNotesContent.length} characters`,
                display_name: 'Notes Content',
              },
            ],
            metadata: {
              affected_user_ids: [userId, currentDoc.owner_id],
              affected_workflow_ids: currentDoc.workflow_ids || [],
              affected_employee_ids: [],
              tags: ['workflow', 'team_notes', 'updated'],
              visibility: 'team',
            },
          });
        } catch (eventError) {
          logger.error('Failed to emit team_notes.updated event', eventError);
          // Don't throw - event emission failure shouldn't break the main operation
        }
      }
    } catch (error) {
      console.error('Error updating team:', error);
      throw error;
    }
  }

  /**
   * Move team to a different program while retaining workflow data
   */
  async moveTeamToProgram(
    teamId: string,
    newProgramId: string,
    userId: string,
    userName: string,
    _auditContext: AuditContext
  ): Promise<void> {
    try {
      // Get team to verify it exists
      const team = await this.teamService.get(teamId);
      if (!team) {
        throw new Error(`Team not found: ${teamId}`);
      }

      // Get old program to remove team from its team_ids
      const oldProgram = await this.programService.get(team.program_id);
      if (!oldProgram) {
        throw new Error(`Old program not found: ${team.program_id}`);
      }

      // Get new program to add team to its team_ids
      const newProgram = await this.programService.get(newProgramId);
      if (!newProgram) {
        throw new Error(`New program not found: ${newProgramId}`);
      }

      const now = Timestamp.now();

      // Use batch to update team and both programs atomically
      const batch = writeBatch(db);

      // Update team with new program info
      const teamRef = doc(db, COLLECTION_NAMES.WORKFLOW_TEAM_TEAMS, teamId);
      batch.update(teamRef, {
        program_id: newProgramId,
        program_name: newProgram.program_name,
        updated_at: now,
        last_modified_by: userId,
        last_modified_by_name: userName,
        version: team.version + 1,
      });

      // Remove team from old program's team_ids (atomic — no read-modify-write race)
      const oldProgramRef = doc(db, COLLECTION_NAMES.WORKFLOW_TEAM_PROGRAMS, team.program_id);
      batch.update(oldProgramRef, {
        team_ids: arrayRemove(teamId),
        updated_at: now,
        last_modified_by: userId,
        last_modified_by_name: userName,
      });

      // Add team to new program's team_ids (atomic — no read-modify-write race)
      const newProgramRef = doc(db, COLLECTION_NAMES.WORKFLOW_TEAM_PROGRAMS, newProgramId);
      batch.update(newProgramRef, {
        team_ids: arrayUnion(teamId),
        updated_at: now,
        last_modified_by: userId,
        last_modified_by_name: userName,
      });

      // Update all workflows for this team to reference the new program
      const workflows = await this.getWorkflowsByTeam(teamId);
      for (const workflow of workflows) {
        const workflowRef = doc(db, COLLECTION_NAMES.WORKFLOW_TEAM_WORKFLOWS, workflow.id);
        batch.update(workflowRef, {
          program_id: newProgramId,
          program_name: newProgram.program_name,
          updated_at: now,
          last_modified_by: userId,
          last_modified_by_name: userName,
          version: workflow.version + 1,
        });
      }

      // Update all workflow spots and hiring spots for this team to reference the new program
      const [workflowSpots, hiringSpots] = await Promise.all([
        workflowSpotService.getWorkflowSpotsByTeam(teamId),
        hiringSpotService.getHiringSpotsByTeam(teamId),
      ]);

      for (const spot of workflowSpots) {
        const spotRef = doc(db, COLLECTION_NAMES.WORKFLOW_SPOTS, spot.id);
        batch.update(spotRef, {
          program_id: newProgramId,
          program_name: newProgram.program_name,
          updated_at: now,
        });
      }

      for (const spot of hiringSpots) {
        const spotRef = doc(db, COLLECTION_NAMES.HIRING_SPOTS, spot.id);
        batch.update(spotRef, {
          program_id: newProgramId,
          program_name: newProgram.program_name,
          updated_at: now,
        });
      }

      await batch.commit();
      
      // Emit activity event for team change (move to different program)
      try {
        await activityEventService.emitUpdate({
          domain: 'workflow',
          event_type: 'team.changed',
          actor: {
            user_id: userId,
            user_name: userName,
          },
          subject: {
            resource_type: 'team',
            resource_id: teamId,
            resource_name: team.team_name,
          },
          message: `Moved team '${team.team_name}' to ${newProgram.program_name}'s program`,
          context: {
            program_id: newProgramId,
            program_name: newProgram.program_name,
            team_id: teamId,
            team_name: team.team_name,
          },
          fields_changed: [
            {
              field_name: 'program_id',
              old_value: team.program_id,
              new_value: newProgramId,
              display_name: 'Program',
            },
            {
              field_name: 'program_name',
              old_value: team.program_name,
              new_value: newProgram.program_name,
              display_name: 'Program Name',
            },
          ],
          previous_state: {
            program_id: team.program_id,
            program_name: team.program_name,
          },
          new_state: {
            program_id: newProgramId,
            program_name: newProgram.program_name,
          },
          metadata: {
            affected_user_ids: [userId, team.owner_id],
            affected_workflow_ids: workflows.map(w => w.id),
            affected_employee_ids: [],
            tags: ['workflow', 'team', 'updated', 'move', 'program-change'],
            visibility: 'program',
          },
        });
      } catch (eventError) {
        logger.error('Failed to emit team.changed event', eventError);
        // Don't throw - event emission failure shouldn't break the main operation
      }
    } catch (error) {
      console.error('Error moving team to program:', error);
      throw error;
    }
  }

  /**
   * Move workflow to a different team and program
   */
  async moveWorkflowToTeam(
    workflowId: string,
    newTeamId: string,
    newProgramId: string,
    userId: string,
    userName: string,
    _auditContext: AuditContext
  ): Promise<void> {
    try {
      // Get workflow to verify it exists
      const workflow = await this.workflowService.get(workflowId);
      if (!workflow) {
        throw new Error(`Workflow not found: ${workflowId}`);
      }

      // Get new team to verify it exists and get team name
      const newTeam = await this.teamService.get(newTeamId);
      if (!newTeam) {
        throw new Error(`New team not found: ${newTeamId}`);
      }

      // Verify the team belongs to the specified program
      if (newTeam.program_id !== newProgramId) {
        throw new Error(`Team ${newTeamId} does not belong to program ${newProgramId}`);
      }

      // Get new program to get program name
      const newProgram = await this.programService.get(newProgramId);
      if (!newProgram) {
        throw new Error(`New program not found: ${newProgramId}`);
      }

      const now = Timestamp.now();

      // Update workflow with new team and program info
      const workflowRef = doc(db, COLLECTION_NAMES.WORKFLOW_TEAM_WORKFLOWS, workflowId);
      await setDoc(
        workflowRef,
        {
          team_id: newTeamId,
          team_name: newTeam.team_name,
          program_id: newProgramId,
          program_name: newProgram.program_name,
          updated_at: now,
          last_modified_by: userId,
          last_modified_by_name: userName,
          version: workflow.version + 1,
        },
        { merge: true }
      );

      // Note: Activity event emission skipped for workflow moves
      // Could be added later with appropriate event type
    } catch (error) {
      console.error('Error moving workflow to team:', error);
      throw error;
    }
  }

  /**
   * Delete team (and all associated workflows)
   */
  async deleteTeam(
    teamId: string,
    _auditContext: AuditContext
  ): Promise<void> {
    try {
      // Get team to verify it exists
      const team = await this.teamService.get(teamId);
      if (!team) {
        throw new Error(`Team not found: ${teamId}`);
      }

      // Get all workflows, workflow spots, and hiring spots for this team
      const [workflows, workflowSpots, hiringSpots] = await Promise.all([
        this.getWorkflowsByTeam(teamId),
        workflowSpotService.getWorkflowSpotsByTeam(teamId),
        hiringSpotService.getHiringSpotsByTeam(teamId),
      ]);

      // Collect all affected user IDs from workflow assignments
      const affectedUserIds = new Set<string>();
      affectedUserIds.add(team.owner_id); // Add team owner

      workflows.forEach(workflow => {
        if (workflow.first_responder) affectedUserIds.add(workflow.first_responder);
        if (workflow.production_support_1) affectedUserIds.add(workflow.production_support_1);
        if (workflow.production_support_2) affectedUserIds.add(workflow.production_support_2);
        if (workflow.strategy_pfm_support) affectedUserIds.add(workflow.strategy_pfm_support);
      });

      // Capture team state before deletion
      const previousState = {
        team_name: team.team_name,
        owner_id: team.owner_id,
        owner_name: team.owner_name,
        program_id: team.program_id,
        program_name: team.program_name,
        workflow_count: workflows.length,
        workflow_spot_count: workflowSpots.length,
        hiring_spot_count: hiringSpots.length,
      };

      // Use batch to delete team, workflows, spots, and update program
      const batch = writeBatch(db);

      // Delete all workflows for this team
      for (const workflow of workflows) {
        const workflowRef = doc(db, COLLECTION_NAMES.WORKFLOW_TEAM_WORKFLOWS, workflow.id);
        batch.delete(workflowRef);
      }

      // Delete all workflow spots and hiring spots for this team
      for (const spot of workflowSpots) {
        const spotRef = doc(db, COLLECTION_NAMES.WORKFLOW_SPOTS, spot.id);
        batch.delete(spotRef);
      }

      for (const spot of hiringSpots) {
        const spotRef = doc(db, COLLECTION_NAMES.HIRING_SPOTS, spot.id);
        batch.delete(spotRef);
      }

      // Delete team
      const teamRef = doc(db, COLLECTION_NAMES.WORKFLOW_TEAM_TEAMS, teamId);
      batch.delete(teamRef);

      // Update program's team_ids (atomic — no read-modify-write race)
      const programRef = doc(db, COLLECTION_NAMES.WORKFLOW_TEAM_PROGRAMS, team.program_id);
      batch.update(programRef, {
        team_ids: arrayRemove(teamId),
        updated_at: Timestamp.now(),
      });

      await batch.commit();
      
      // Emit activity event for team removal
      try {
        await activityEventService.emitDeletion({
          domain: 'workflow',
          event_type: 'team.removed',
          actor: {
            user_id: _auditContext.userId,
            user_name: _auditContext.userName,
          },
          subject: {
            resource_type: 'team',
            resource_id: teamId,
            resource_name: team.team_name,
          },
          message: `Removed team '${team.team_name}'${team.program_name ? ` from ${team.program_name}` : ''}`,
          context: {
            program_id: team.program_id,
            program_name: team.program_name,
          },
          previous_state: previousState,
          metadata: {
            affected_user_ids: Array.from(affectedUserIds),
            affected_workflow_ids: workflows.map(w => w.id),
            affected_employee_ids: [],
            tags: ['workflow', 'team', 'removed'],
            visibility: 'program',
          },
        });
      } catch (eventError) {
        logger.error('Failed to emit team.removed event', eventError);
        // Don't throw - event emission failure shouldn't break the main operation
      }
    } catch (error) {
      console.error('Error deleting team:', error);
      throw error;
    }
  }

  /**
   * Get teams by program
   */
  async getTeamsByProgram(programId: string): Promise<TeamData[]> {
    try {
      const constraints = [
        where('program_id', '==', programId),
        orderBy('updated_at', 'desc')
      ];
      return await this.teamService.list(constraints);
    } catch (error) {
      console.error('Error getting teams by program:', error);
      // Return empty array instead of throwing to prevent infinite loading
      return [];
    }
  }

  /**
   * Get all teams across all programs
   */
  async getAllTeams(): Promise<TeamData[]> {
    try {
      const constraints = [orderBy('updated_at', 'desc')];
      return await this.teamService.list(constraints);
    } catch (error) {
      console.error('Error getting all teams:', error);
      throw error;
    }
  }

  // ============================================================================
  // WORKFLOW OPERATIONS
  // ============================================================================

  /**
   * Create a new workflow
   */
  async createWorkflow(
    data: CreateWorkflowData,
    userId: string,
    userName: string,
    _auditContext: AuditContext
  ): Promise<string> {
    try {
      // Validate input data
      const validatedData = CreateWorkflowDataSchema.parse(data);
      
      const now = Timestamp.now();
      
      // Verify team exists
      const team = await this.teamService.get(validatedData.team_id);
      if (!team) {
        throw new Error(`Team not found: ${validatedData.team_id}`);
      }

      // Check if workflow already exists (in any team)
      const existingWorkflow = await this.workflowService.get(validatedData.workflow_id);
      if (existingWorkflow) {
        // Get the team name where this workflow currently exists
        const existingTeam = await this.teamService.get(existingWorkflow.team_id);
        const existingTeamName = existingTeam?.team_name || 'another team';
        const errorMsg = `Failed to create workflow. This workflow already exists in ${existingTeamName}'s Team.`;
        console.error('[threeCollectionWorkflowService] Duplicate workflow detected:', errorMsg, {
          workflowId: validatedData.workflow_id,
          existingTeamId: existingWorkflow.team_id,
          existingTeamName,
          attemptedTeamId: validatedData.team_id,
          attemptedTeamName: team.team_name,
        });
        throw new Error(errorMsg);
      }

      const workflowBase = {
        workflow_id: validatedData.workflow_id,
        workflow_name: validatedData.workflow_name,
        team_id: validatedData.team_id,
        team_name: validatedData.team_name,
        program_id: validatedData.program_id,
        program_name: validatedData.program_name,
        first_responder: validatedData.first_responder || null,
        production_support_1: validatedData.production_support_1 || null,
        production_support_2: validatedData.production_support_2 || null,
        strategy_pfm_support: validatedData.strategy_pfm_support || null,
        status: validatedData.status || 'planning',
        priority: validatedData.priority || 'medium',
        // Enhanced workflow spot fields required by Firestore rules
        approval_status: 'draft' as const,
        complexity_level: 'medium' as const,
        order: team.workflow_ids.length, // Use current workflow count as order
        created_by: userId,
        created_by_name: userName,
        created_at: now,
        updated_at: now,
        last_modified_by: userId,
        last_modified_by_name: userName,
        version: 1,
        ...(validatedData.metadata && { metadata: validatedData.metadata }),
      };

      // Only add optional fields if they have values
      const workflow: any = { ...workflowBase };
      if (validatedData.estimated_hours !== undefined) {
        workflow.estimated_hours = validatedData.estimated_hours;
      }
      if (validatedData.actual_hours !== undefined) {
        workflow.actual_hours = validatedData.actual_hours;
      }
      if (validatedData.notes !== undefined) {
        workflow.notes = validatedData.notes;
      }
      if (validatedData.workflow_config_ref !== undefined) {
        workflow.workflow_config_ref = validatedData.workflow_config_ref;
      }

      // Use batch to create workflow and update team's workflow_ids
      const batch = writeBatch(db);

      // Create workflow with the provided workflow_id as document ID
      const workflowId = validatedData.workflow_id;
      const workflowRef = doc(db, COLLECTION_NAMES.WORKFLOW_TEAM_WORKFLOWS, workflowId);
      batch.set(workflowRef, { ...workflow, id: workflowId });

      // Update team's workflow_ids
      const teamRef = doc(db, COLLECTION_NAMES.WORKFLOW_TEAM_TEAMS, validatedData.team_id);
      batch.update(teamRef, {
        workflow_ids: [...team.workflow_ids, workflowId],
        updated_at: now,
        last_modified_by: userId,
        last_modified_by_name: userName,
        version: team.version + 1,
      });

      await batch.commit();
      
      // Emit activity event for workflow creation
      try {
        // Collect affected user IDs (all should be internal system user IDs)
        const affectedUserIds = new Set<string>([userId, team.owner_id]);
        if (validatedData.first_responder) affectedUserIds.add(validatedData.first_responder);
        if (validatedData.production_support_1) affectedUserIds.add(validatedData.production_support_1);
        if (validatedData.production_support_2) affectedUserIds.add(validatedData.production_support_2);
        if (validatedData.strategy_pfm_support) affectedUserIds.add(validatedData.strategy_pfm_support);
        
        await activityEventService.emit({
          domain: 'workflow',
          event_type: 'workflow.assigned',
          actor: {
            user_id: userId,
            user_name: userName,
          },
          subject: {
            resource_type: 'workflow',
            resource_id: workflowId,
            resource_name: validatedData.workflow_name,
          },
          message: `Assigned workflow '${validatedData.workflow_name}'${validatedData.team_name ? ` to ${validatedData.team_name}'s team` : ''}`,
          context: {
            program_id: validatedData.program_id,
            program_name: validatedData.program_name,
            team_id: validatedData.team_id,
            team_name: validatedData.team_name,
          },
          change: {
            action: 'assigned',
            new_state: {
              workflow_name: validatedData.workflow_name,
              team_id: validatedData.team_id,
              team_name: validatedData.team_name,
              program_id: validatedData.program_id,
              program_name: validatedData.program_name,
              first_responder: validatedData.first_responder,
              production_support_1: validatedData.production_support_1,
              production_support_2: validatedData.production_support_2,
              strategy_pfm_support: validatedData.strategy_pfm_support,
            },
          },
          metadata: {
            affected_user_ids: Array.from(affectedUserIds),
            affected_workflow_ids: [workflowId],
            affected_employee_ids: [],
            tags: ['workflow', 'workflow', 'assigned'],
            visibility: 'team',
          },
        });
        
      } catch (eventError) {
        logger.error('Failed to emit workflow.assigned event', eventError);
        // Don't throw - event emission failure shouldn't break the main operation
      }

      return workflowId;
    } catch (error) {
      console.error('Error creating workflow:', error);
      // Re-throw the error with its message intact
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Failed to create workflow: ' + String(error));
    }
  }

  /**
   * Get workflow by ID
   */
  async getWorkflow(workflowId: string): Promise<WorkflowData | null> {
    try {
      return await this.workflowService.get(workflowId);
    } catch (error) {
      console.error('Error getting workflow:', error);
      throw error;
    }
  }

  /**
   * Update workflow
   */
  async updateWorkflow(
    workflowId: string,
    data: UpdateWorkflowData,
    userId: string,
    userName: string,
    auditContext: AuditContext
  ): Promise<void> {
    try {
      const now = Timestamp.now();

      // Get current workflow to track changes
      const currentDoc = await this.workflowService.get(workflowId);
      if (!currentDoc) {
        throw new Error(`Workflow not found: ${workflowId}`);
      }

      const updatePayload: Partial<WorkflowData> = {
        updated_at: now,
        last_modified_by: userId,
        last_modified_by_name: userName,
      };

      // Track assignment changes for activity events
      const assignmentChanges: Array<{ field_name: string; old_value: string | null; new_value: string | null; display_name: string }> = [];
      
      // Add all possible fields from UpdateWorkflowData
      if (data.workflow_name) updatePayload.workflow_name = data.workflow_name;
      
      if (data.first_responder !== undefined) {
        updatePayload.first_responder = data.first_responder;
        if (currentDoc.first_responder !== data.first_responder) {
          assignmentChanges.push({
            field_name: 'first_responder',
            old_value: currentDoc.first_responder,
            new_value: data.first_responder,
            display_name: 'First Responder',
          });
        }
      }
      
      if (data.production_support_1 !== undefined) {
        updatePayload.production_support_1 = data.production_support_1;
        if (currentDoc.production_support_1 !== data.production_support_1) {
          assignmentChanges.push({
            field_name: 'production_support_1',
            old_value: currentDoc.production_support_1,
            new_value: data.production_support_1,
            display_name: 'Production Support 1',
          });
        }
      }
      
      if (data.production_support_2 !== undefined) {
        updatePayload.production_support_2 = data.production_support_2;
        if (currentDoc.production_support_2 !== data.production_support_2) {
          assignmentChanges.push({
            field_name: 'production_support_2',
            old_value: currentDoc.production_support_2,
            new_value: data.production_support_2,
            display_name: 'Production Support 2',
          });
        }
      }
      
      if (data.strategy_pfm_support !== undefined) {
        updatePayload.strategy_pfm_support = data.strategy_pfm_support;
        if (currentDoc.strategy_pfm_support !== data.strategy_pfm_support) {
          assignmentChanges.push({
            field_name: 'strategy_pfm_support',
            old_value: currentDoc.strategy_pfm_support,
            new_value: data.strategy_pfm_support,
            display_name: 'Strategy & PFM Support',
          });
        }
      }
      
      if (data.status) updatePayload.status = data.status;
      if (data.priority) updatePayload.priority = data.priority;
      if (data.estimated_hours !== undefined) updatePayload.estimated_hours = data.estimated_hours;
      if (data.actual_hours !== undefined) updatePayload.actual_hours = data.actual_hours;
      if (data.notes !== undefined) updatePayload.notes = data.notes;
      if (data.workflow_config_ref !== undefined) updatePayload.workflow_config_ref = data.workflow_config_ref;
      if (data.metadata) updatePayload.metadata = data.metadata;

      // Handle version increment
      updatePayload.version = currentDoc.version + 1;

      await this.workflowService.update(workflowId, updatePayload, auditContext);

      // Emit activity event for assignment changes
      if (assignmentChanges.length > 0) {
        try {
          // Collect affected user IDs (all should be internal system user IDs)
          const affectedUserIds = new Set<string>([userId]);

          // Add old and new assigned users
          assignmentChanges.forEach(change => {
            if (change.old_value) affectedUserIds.add(change.old_value);
            if (change.new_value) affectedUserIds.add(change.new_value);
          });

          // Add team owner
          const team = await this.teamService.get(currentDoc.team_id);
          if (team) {
            affectedUserIds.add(team.owner_id);
          }
          
          // Construct change object with only defined fields
          const changeObject: any = {
            action: 'assigned' as const,
            fields_changed: assignmentChanges,
          };
          
          await activityEventService.emit({
            domain: 'workflow',
            event_type: 'workflow.assigned',
            actor: {
              user_id: userId,
              user_name: userName,
            },
            subject: {
              resource_type: 'workflow',
              resource_id: workflowId,
              resource_name: currentDoc.workflow_name,
            },
            message: `Updated assignments for workflow '${currentDoc.workflow_name}'${currentDoc.team_name ? ` in ${currentDoc.team_name}` : ''}`,
            change: changeObject,
            context: {
              program_id: currentDoc.program_id,
              program_name: currentDoc.program_name,
              team_id: currentDoc.team_id,
              team_name: currentDoc.team_name,
            },
            metadata: {
              affected_user_ids: Array.from(affectedUserIds),
              affected_workflow_ids: [workflowId],
              affected_employee_ids: [],
              tags: ['workflow', 'workflow', 'assigned', 'update'],
              visibility: 'team',
            },
          });
          
        } catch (eventError) {
          logger.error('Failed to emit workflow.assigned event', eventError);
          // Don't throw - event emission failure shouldn't break the main operation
        }
      }
    } catch (error) {
      console.error('Error updating workflow:', error);
      throw error;
    }
  }

  /**
   * Delete workflow
   */
  async deleteWorkflow(
    workflowId: string,
    auditContext: AuditContext
  ): Promise<void> {
    try {
      // Get workflow to verify it exists and capture state before deletion
      const workflow = await this.workflowService.get(workflowId);
      if (!workflow) {
        throw new Error(`Workflow not found: ${workflowId}`);
      }

      // Get team to update workflow_ids
      const team = await this.teamService.get(workflow.team_id);
      if (!team) {
        throw new Error(`Team not found: ${workflow.team_id}`);
      }

      // Collect all affected user IDs from workflow assignments
      const affectedUserIds = new Set<string>();
      affectedUserIds.add(auditContext.userId); // User performing deletion
      affectedUserIds.add(team.owner_id); // Team owner
      if (workflow.first_responder) affectedUserIds.add(workflow.first_responder);
      if (workflow.production_support_1) affectedUserIds.add(workflow.production_support_1);
      if (workflow.production_support_2) affectedUserIds.add(workflow.production_support_2);
      if (workflow.strategy_pfm_support) affectedUserIds.add(workflow.strategy_pfm_support);

      // Use batch to delete workflow and update team
      const batch = writeBatch(db);

      // Delete workflow
      const workflowRef = doc(db, COLLECTION_NAMES.WORKFLOW_TEAM_WORKFLOWS, workflowId);
      batch.delete(workflowRef);

      // Update team's workflow_ids
      const updatedWorkflowIds = team.workflow_ids.filter(id => id !== workflowId);
      const teamRef = doc(db, COLLECTION_NAMES.WORKFLOW_TEAM_TEAMS, workflow.team_id);
      batch.update(teamRef, {
        workflow_ids: updatedWorkflowIds,
        updated_at: Timestamp.now(),
        version: team.version + 1,
      });

      await batch.commit();
      
      // Emit activity event for workflow unassignment (deletion)
      try {
        // Build fields changed to show all assignments being removed
        const fieldsChanged: Array<{ field_name: string; old_value: string | null; new_value: null; display_name: string }> = [];
        
        if (workflow.first_responder) {
          fieldsChanged.push({
            field_name: 'first_responder',
            old_value: workflow.first_responder,
            new_value: null,
            display_name: 'First Responder',
          });
        }
        
        if (workflow.production_support_1) {
          fieldsChanged.push({
            field_name: 'production_support_1',
            old_value: workflow.production_support_1,
            new_value: null,
            display_name: 'Production Support 1',
          });
        }
        
        if (workflow.production_support_2) {
          fieldsChanged.push({
            field_name: 'production_support_2',
            old_value: workflow.production_support_2,
            new_value: null,
            display_name: 'Production Support 2',
          });
        }
        
        if (workflow.strategy_pfm_support) {
          fieldsChanged.push({
            field_name: 'strategy_pfm_support',
            old_value: workflow.strategy_pfm_support,
            new_value: null,
            display_name: 'Strategy & PFM Support',
          });
        }
        
        const changeObject: any = {
          action: 'unassigned' as const,
        };
        
        // Only add fields_changed if there are any assignments to track
        if (fieldsChanged.length > 0) {
          changeObject.fields_changed = fieldsChanged;
        }
        
        await activityEventService.emit({
          domain: 'workflow',
          event_type: 'workflow.unassigned',
          actor: {
            user_id: auditContext.userId,
            user_name: auditContext.userName,
          },
          subject: {
            resource_type: 'workflow',
            resource_id: workflowId,
            resource_name: workflow.workflow_name,
          },
          message: `Unassigned workflow '${workflow.workflow_name}'${workflow.team_name ? ` from ${workflow.team_name}` : ''}`,
          change: changeObject,
          context: {
            program_id: workflow.program_id,
            program_name: workflow.program_name,
            team_id: workflow.team_id,
            team_name: workflow.team_name,
          },
          metadata: {
            affected_user_ids: Array.from(affectedUserIds),
            affected_workflow_ids: [workflowId],
            affected_employee_ids: [],
            tags: ['workflow', 'workflow', 'unassigned'],
            visibility: 'team',
          },
        });
        
      } catch (eventError) {
        logger.error('Failed to emit workflow.unassigned event', eventError);
        // Don't throw - event emission failure shouldn't break the main operation
      }
    } catch (error) {
      console.error('Error deleting workflow:', error);
      throw error;
    }
  }

  /**
   * Get workflows by team
   */
  async getWorkflowsByTeam(teamId: string): Promise<WorkflowData[]> {
    const constraints = [
      where('team_id', '==', teamId),
      orderBy('updated_at', 'desc')
    ];
    return await this.workflowService.list(constraints);
  }

  /**
   * Get workflows by program
   */
  async getWorkflowsByProgram(programId: string): Promise<WorkflowData[]> {
    try {
      const constraints = [
        where('program_id', '==', programId),
        orderBy('updated_at', 'desc')
      ];
      return await this.workflowService.list(constraints);
    } catch (error) {
      console.error('Error getting workflows by program:', error);
      throw error;
    }
  }

  /**
   * Get all workflows across all teams and programs
   */
  async getAllWorkflows(): Promise<WorkflowData[]> {
    try {
      const constraints = [orderBy('updated_at', 'desc')];
      return await this.workflowService.list(constraints);
    } catch (error) {
      console.error('Error getting all workflows:', error);
      throw error;
    }
  }

  // ============================================================================
  // AGGREGATION METHODS (for UI components)
  // ============================================================================

  /**
   * Get program info with aggregated data
   */
  /**
   * Get programs info with optimized batch queries to eliminate N+1 problem
   *
   * PERFORMANCE OPTIMIZATION:
   * - Old: 1 + N + M queries (N=programs, M=teams) = ~13+ queries for 6 programs
   * - New: 3 queries total (programs + all teams + all workflows)
   * - Expected improvement: ~5000ms -> ~200ms
   */
  async getProgramsInfo(): Promise<ProgramInfo[]> {
    try {
      
      // Step 1: Get all programs (1 query)
      const programs = await this.getAllPrograms();
      
      if (programs.length === 0) {
        return [];
      }

      // Step 2: Get ALL teams in one batch query (1 query instead of N)
      const programIds = programs.map(p => p.id);
      const allTeamsConstraints = [
        where('program_id', 'in', programIds),
        orderBy('updated_at', 'desc')
      ];
      const allTeams = await this.teamService.list(allTeamsConstraints);

      // Step 3: Get ALL workflows in one batch query (1 query instead of M)
      const allWorkflowsConstraints = [
        where('program_id', 'in', programIds),
        orderBy('updated_at', 'desc')
      ];
      const allWorkflows = await this.workflowService.list(allWorkflowsConstraints);

      // Step 4: Group data by program_id for O(1) lookups
      const teamsByProgram = new Map<string, TeamData[]>();
      const workflowsByProgram = new Map<string, number>();

      // Group teams by program
      allTeams.forEach(team => {
        if (!teamsByProgram.has(team.program_id)) {
          teamsByProgram.set(team.program_id, []);
        }
        teamsByProgram.get(team.program_id)!.push(team);
      });

      // Count workflows by program
      allWorkflows.forEach(workflow => {
        const currentCount = workflowsByProgram.get(workflow.program_id) || 0;
        workflowsByProgram.set(workflow.program_id, currentCount + 1);
      });

      // Step 5: Build program info objects
      const programInfos: ProgramInfo[] = programs.map(program => {
        const teams = teamsByProgram.get(program.id) || [];
        const workflowCount = workflowsByProgram.get(program.id) || 0;

        return {
          program_id: program.id,
          program_name: program.program_name,
          owner_id: program.owner_id,
          owner_name: program.owner_name,
          team_count: teams.length,
          workflow_count: workflowCount,
          last_updated: program.updated_at instanceof Date ? program.updated_at :
                       (program.updated_at?.toDate ? program.updated_at.toDate() : new Date()),
        };
      });

      return programInfos.sort((a, b) => a.program_name.localeCompare(b.program_name));
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get team info with aggregated data for a program
   */
  async getTeamsInfo(programId: string): Promise<TeamInfo[]> {
    try {
      const teams = await this.getTeamsByProgram(programId);
      const teamInfos: TeamInfo[] = [];

      for (const team of teams) {
        // Graceful degradation: if workflow query fails, show team with zero counts
        let workflows: WorkflowData[] = [];
        try {
          workflows = await this.getWorkflowsByTeam(team.id);
        } catch {
          // Failed to load workflows for team — showing zero counts
        }

        // Count assignments across all workflows
        let assignmentCount = 0;
        for (const workflow of workflows) {
          if (workflow.first_responder) assignmentCount++;
          if (workflow.production_support_1) assignmentCount++;
          if (workflow.production_support_2) assignmentCount++;
          if (workflow.strategy_pfm_support) assignmentCount++;
        }

        teamInfos.push({
          team_id: team.id,
          team_name: team.team_name,
          owner_id: team.owner_id,
          owner_name: team.owner_name,
          program_id: team.program_id,
          program_name: team.program_name,
          workflow_count: workflows.length,
          assignment_count: assignmentCount,
          slack_channel_id: team.slack_channel_id,
          last_updated: team.updated_at instanceof Date ? team.updated_at :
                       (team.updated_at?.toDate ? team.updated_at.toDate() : new Date()),
        });
      }

      return teamInfos.sort((a, b) => a.team_name.localeCompare(b.team_name));
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get workflow info for a team
   */
  async getWorkflowsInfo(teamId: string): Promise<WorkflowInfo[]> {
    try {
      const workflows = await this.getWorkflowsByTeam(teamId);
      
      return workflows.map(workflow => ({
        workflow_id: workflow.id,
        workflow_name: workflow.workflow_name,
        team_id: workflow.team_id,
        team_name: workflow.team_name,
        program_id: workflow.program_id,
        program_name: workflow.program_name,
        first_responder: workflow.first_responder,
        production_support_1: workflow.production_support_1,
        production_support_2: workflow.production_support_2,
        strategy_pfm_support: workflow.strategy_pfm_support,
        status: workflow.status,
        priority: workflow.priority,
        last_updated: workflow.updated_at instanceof Date ? workflow.updated_at :
                     (workflow.updated_at?.toDate ? workflow.updated_at.toDate() : new Date()),
      })).sort((a, b) => a.workflow_name.localeCompare(b.workflow_name));
    } catch (error) {
      console.error('Error getting workflows info:', error);
      throw error;
    }
  }

  /**
   * Get ALL workflow info globally (across all teams and programs)
   * Includes both legacy workflows and workflow spots for accurate workload calculations.
   */
  async getAllWorkflowsInfo(): Promise<WorkflowInfo[]> {
    try {
      const [allWorkflows, allSpots] = await Promise.all([
        this.workflowService.list([]),
        workflowSpotService.getAllWorkflowSpots(),
      ]);

      const legacyMapped: WorkflowInfo[] = allWorkflows.map((workflow: WorkflowData & { id: string }) => ({
        workflow_id: workflow.id,
        workflow_name: workflow.workflow_name,
        team_id: workflow.team_id,
        team_name: workflow.team_name,
        program_id: workflow.program_id,
        program_name: workflow.program_name,
        first_responder: workflow.first_responder,
        production_support_1: workflow.production_support_1,
        production_support_2: workflow.production_support_2,
        strategy_pfm_support: workflow.strategy_pfm_support,
        status: workflow.status,
        priority: workflow.priority,
        last_updated: workflow.updated_at instanceof Date ? workflow.updated_at :
                     (workflow.updated_at?.toDate ? workflow.updated_at.toDate() : new Date()),
      }));

      const spotsMapped: WorkflowInfo[] = allSpots.map(spot => ({
        workflow_id: spot.id,
        workflow_name: spot.workflow_name,
        team_id: spot.team_id,
        team_name: spot.team_name,
        program_id: spot.program_id,
        program_name: spot.program_name,
        first_responder: spot.first_responder,
        production_support_1: spot.production_support_1,
        production_support_2: spot.production_support_2,
        strategy_pfm_support: spot.strategy_pfm_support,
        status: 'active',
        priority: 'medium',
        last_updated: spot.updated_at?.toDate ? spot.updated_at.toDate() : new Date(),
      }));

      return [...legacyMapped, ...spotsMapped].sort((a, b) =>
        a.workflow_name.localeCompare(b.workflow_name)
      );
    } catch (error) {
      console.error('Error getting all workflows info:', error);
      throw error;
    }
  }

  // ============================================================================
  // REAL-TIME SUBSCRIPTIONS
  // ============================================================================

  /**
   * Subscribe to program updates
   */
  subscribeToProgramUpdates(callback: (programs: ProgramData[]) => void): () => void {
    const constraints = [orderBy('updated_at', 'desc')];
    return this.programService.subscribeToQuery(constraints, callback);
  }

  /**
   * Subscribe to team updates for a program
   */
  subscribeToTeamUpdates(programId: string, callback: (teams: TeamData[]) => void): () => void {
    const constraints = [
      where('program_id', '==', programId),
      orderBy('updated_at', 'desc')
    ];
    return this.teamService.subscribeToQuery(constraints, callback);
  }

  /**
   * Subscribe to workflow updates for a team
   */
  subscribeToWorkflowUpdates(teamId: string, callback: (workflows: WorkflowData[]) => void): () => void {
    const constraints = [
      where('team_id', '==', teamId),
      orderBy('updated_at', 'desc')
    ];
    return this.workflowService.subscribeToQuery(constraints, callback);
  }
}

// Export singleton instance
export const threeCollectionWorkflowService = new ThreeCollectionWorkflowService();